import { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';

export const SelectGroupOption = ({
  group,
  selectedItems,
  onGroupHeaderClick,
  onChildClick,
}) => {
  const checkboxRef = useRef(null);

  // All selectable items = parent city + its neighbors
  const items = useMemo(
    () => [
      { id: group.id, value: group.value ?? group.id, title: group.title },
      ...group.city_neighbors.map(n => ({
        id: n.id,
        value: n.value ?? n.id,
        title: n.name ?? n.title,
      })),
    ],
    [group]
  );

  const isItemSelected = item =>
    selectedItems.some(s => s.value === item.value || s.id === item.id);

  const selectedCount = useMemo(
    () => items.filter(isItemSelected).length,
    [items, selectedItems]
  );

  const allSelected = selectedCount === items.length && items.length > 0;
  const someSelected = selectedCount > 0 && !allSelected;

  // indeterminate can only be set imperatively
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleHeaderClick = e => {
    e.stopPropagation();
    onGroupHeaderClick(group, items, allSelected);
  };

  const handleItemClick = (e, item) => {
    e.stopPropagation();
    onChildClick(item);
  };

  return (
    <>
      {/* Group header — select-all toggle, not itself selectable */}
      <li
        role="option"
        aria-selected={allSelected}
        onClick={handleHeaderClick}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold cursor-pointer select-none hover:bg-[#f5f5f5]"
      >
        <input
          ref={checkboxRef}
          type="checkbox"
          readOnly
          checked={allSelected}
          className="pointer-events-none"
        />
        <span>{group.title}</span>
        <span className="text-xs font-normal text-gray-400 ml-auto">
          {selectedCount > 0 ? `${selectedCount}/${items.length}` : items.length}
        </span>
      </li>

      {/* All items: parent city + neighbors, indented */}
      {items.map(item => (
        <li
          key={item.id}
          role="option"
          aria-selected={isItemSelected(item)}
          onClick={e => handleItemClick(e, item)}
          className={cn(
            'flex items-center gap-2 pl-7 pr-3 py-1.5 text-sm cursor-pointer select-none',
            'hover:bg-[#f5f5f5]'
          )}
        >
          <input
            type="checkbox"
            readOnly
            checked={isItemSelected(item)}
            className="pointer-events-none"
          />
          <span>{item.title}</span>
        </li>
      ))}
    </>
  );
};
